// Serialize decoding across multi-selection and crop uploads to bound canvas memory.
let resizeQueue = Promise.resolve();
export const ORIGINAL_IMAGE_MAX_BYTES = 20 * 1024 * 1024;

const positiveNumber = (value, fallback, max) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 && number <= max ? number : fallback;
};

export const formatBytes = bytes => {
  if (bytes == null) return 'unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Universal client-side image resizer before uploading to Sharetribe API.
 * Sharetribe charges 230,000 credits per GB of upload data transfer.
 */
export const resizeImage = (file, options = {}) => {
  const isResizeEnabled = process.env.REACT_APP_IMAGE_RESIZE_ENABLED !== 'false';
  const {
    tag = 'Image',
    defaultMaxEdge = 2048,
    maxEdgeCap = 3072,
    defaultQuality = 0.82,
    envMaxEdge = process.env.REACT_APP_IMAGE_MAX_EDGE,
    envQuality = process.env.REACT_APP_IMAGE_JPEG_QUALITY,
  } = options;

  const task = async () => {
    // Check if compression is disabled or file is invalid
    if (
      !isResizeEnabled ||
      typeof document === 'undefined' ||
      typeof window === 'undefined' ||
      !(file instanceof File) ||
      !file.type?.startsWith('image/')
    ) {
      if (!isResizeEnabled) {
        console.log(`[resize${tag}] Skipping: REACT_APP_IMAGE_RESIZE_ENABLED is false.`);
      }
      return file;
    }

    // GIFs and SVGs should not be canvas-downscaled (preserves animation and vector geometry)
    if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
      console.log(
        `[resize${tag}] Skipping "${file.name}": Format is ${file.type} (animation/vector preserved). Size: ${formatBytes(file.size)}`
      );
      return file;
    }

    const maxEdge = positiveNumber(envMaxEdge, defaultMaxEdge, maxEdgeCap);
    const quality = positiveNumber(envQuality, defaultQuality, 1);
    let bitmap;
    let objectUrl;
    let canvas;

    try {
      if (typeof createImageBitmap === 'function') {
        try {
          // Browser applies EXIF once off-thread. No second manual rotation is needed.
          bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch (error) {
          // Fallback to HTMLImageElement for formats not handled by createImageBitmap
        }
      }

      let source = bitmap;
      if (!source) {
        objectUrl = URL.createObjectURL(file);
        source = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = objectUrl;
        });
      }

      const width = bitmap ? bitmap.width : source.naturalWidth;
      const height = bitmap ? bitmap.height : source.naturalHeight;

      // If already within reasonable dimensions and size is small (<500KB), keep original
      if (!width || !height || (Math.max(width, height) <= maxEdge && file.size < 500 * 1024)) {
        console.log(
          `[resize${tag}] Skipping resize for "${file.name}": (${width}x${height}px) <= maxEdge (${maxEdge}px) and size is small (${formatBytes(file.size)}).`
        );
        return file;
      }

      const scale = Math.min(1, maxEdge / Math.max(width, height));
      canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(width * scale));
      canvas.height = Math.max(1, Math.round(height * scale));
      const context = canvas.getContext('2d');
      if (!context) return file;

      context.drawImage(source, 0, 0, canvas.width, canvas.height);

      // Convert heavy PNGs to JPEG for massive space savings (~90% reduction)
      const mimeType = file.type === 'image/png' ? 'image/jpeg' : file.type;
      const blob = await new Promise(resolve => canvas.toBlob(resolve, mimeType, quality));

      if (!blob || blob.size >= file.size) {
        console.log(
          `[resize${tag}] Keeping original for "${file.name}": resized blob (${formatBytes(blob?.size)}) is not smaller than original (${formatBytes(file.size)}).`
        );
        return file;
      }

      const resizedFile = new File(
        [blob],
        file.name.replace(/\.[^.]+$/, mimeType === 'image/jpeg' ? '.jpg' : '$&'),
        {
          type: mimeType,
          lastModified: file.lastModified || Date.now(),
        }
      );

      const savedBytes = file.size - resizedFile.size;
      const reductionPercent = ((savedBytes / file.size) * 100).toFixed(1);

      console.log(
        `%c[resize${tag}] "${file.name}":\n` +
          `  Before: ${width}x${height}px, ${formatBytes(file.size)} (${file.size} bytes)\n` +
          `  After:  ${canvas.width}x${canvas.height}px, ${formatBytes(resizedFile.size)} (${resizedFile.size} bytes)\n` +
          `  Saved:  ${formatBytes(savedBytes)} (-${reductionPercent}%)`,
        'color: #10b981; font-weight: bold;'
      );

      return resizedFile;
    } catch (error) {
      console.warn(`[resize${tag}] Failed to resize "${file.name}":`, error);
      return file;
    } finally {
      if (bitmap) bitmap.close?.();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    }
  };

  const result = resizeQueue.then(task, task);
  resizeQueue = result.then(() => undefined, () => undefined);
  return result;
};

/**
 * Resize listing images (default 2048px @ 82% quality).
 */
export const resizeListingImage = file =>
  resizeImage(file, { tag: 'ListingImage', defaultMaxEdge: 2048 });

/**
 * Resize profile avatar images (default 1024px @ 82% quality).
 * Profile avatars are only displayed up to square-small2x (480x480).
 * Capping to 1024px preserves crisp retina quality while keeping avatar uploads under ~150KB.
 */
export const resizeAvatarImage = file =>
  resizeImage(file, {
    tag: 'AvatarImage',
    defaultMaxEdge: 1024,
    envMaxEdge: process.env.REACT_APP_AVATAR_IMAGE_MAX_EDGE || process.env.REACT_APP_IMAGE_MAX_EDGE,
  });
